import { useState, useEffect, useCallback, useMemo, createContext, useContext, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Mail, BarChart3, Settings, Shield, Users, Play, Square, LogOut, History as HistoryIcon, FileText, PanelLeftClose, PanelLeft, Brain, Zap, FileEdit, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import LoginPage from "./pages/LoginPage";
import { Toaster } from "@/components/ui/sonner";

const Dashboard = lazy(() => import("./pages/Dashboard"));
const Inbox = lazy(() => import("./pages/Inbox"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const AccountsPage = lazy(() => import("./pages/AccountsPage"));
const HistoryPage = lazy(() => import("./pages/HistoryPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const AITrainingPage = lazy(() => import("./pages/AITrainingPage"));
const IntelligencePage = lazy(() => import("./pages/IntelligencePage"));
const TemplatesPage = lazy(() => import("./pages/TemplatesPage"));

const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
  </div>
);

import { API } from "./config/api";

const AccountContext = createContext();
export const useAccount = () => useContext(AccountContext);

const Layout = ({ children }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { selectedAccount, setSelectedAccount, accounts, autoScanActive, toggleAutoScan } = useAccount();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  const navItems = [
    { path: "/", icon: BarChart3, label: "Dashboard" },
    { path: "/inbox", icon: Mail, label: "Inbox" },
    { path: "/accounts", icon: Users, label: "Cuentas" },
    { path: "/history", icon: HistoryIcon, label: "Historia" },
    { path: "/reports", icon: FileText, label: "Reportes" },
    { path: "/ai-training", icon: Brain, label: "Entrenamiento IA" },
    { path: "/intelligence", icon: Zap, label: "MCR Intelligence" },
    { path: "/templates", icon: FileEdit, label: "Plantillas" },
    ...(user?.role === "admin" ? [{ path: "/users", icon: Shield, label: "Usuarios" }] : []),
    { path: "/settings", icon: Settings, label: "Config" },
  ];
  
  return (
    <div className="flex h-screen bg-slate-50">
      <aside className={`bg-white border-r border-slate-200 flex flex-col transition-all duration-300 ${sidebarOpen ? "w-64" : "w-0 overflow-hidden border-r-0"}`}>
        <div className="p-6 border-b border-slate-200 min-w-[16rem]">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 flex items-center justify-center">
              <img src="https://customer-assets.emergentagent.com/job_hostalia-spam-filter/artifacts/49u2e23z_IMG_4055.png" alt="MCR" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="text-xl font-black tracking-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>MailBot</h1>
              <p className="text-xs text-slate-500">MCR Agency</p>
            </div>
          </div>
        </div>
        
        <div className="p-4 border-b border-slate-200">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 block">Cuenta Activa</label>
          <Select value={selectedAccount || "all"} onValueChange={setSelectedAccount}>
            <SelectTrigger data-testid="account-selector" className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {accounts.map((acc) => <SelectItem key={acc.id} value={acc.id}>{acc.account_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="mt-2 text-xs text-slate-500">{accounts.length} cuentas</div>
        </div>
        
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path}>
                  <Link to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}>
                    <Icon className="w-5 h-5" />
                    <span className="font-medium text-sm">{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
        
        <div className="p-4 border-t border-slate-200 space-y-3">
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Auto-Scan</span>
              <Badge className={autoScanActive ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-800"}>{autoScanActive ? "Activo" : "Inactivo"}</Badge>
            </div>
            <Button onClick={toggleAutoScan} size="sm" className="w-full bg-slate-900 text-white hover:bg-slate-800 text-xs py-1.5">
              {autoScanActive ? <><Square className="w-3 h-3 mr-1" />Detener</> : <><Play className="w-3 h-3 mr-1" />Iniciar</>}
            </Button>
          </div>
          <div className="text-xs text-slate-500 text-center">
            <p className="font-medium">{user?.username}</p>
            <p className="text-slate-400">{user?.email}</p>
            <Button onClick={logout} variant="outline" size="sm" className="w-full mt-2 text-xs"><LogOut className="w-3 h-3 mr-1" />Cerrar Sesión</Button>
            <p className="text-slate-300 mt-1" style={{fontSize:'9px'}}>v2.2</p>
          </div>
        </div>
      </aside>
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex items-center h-10 px-2 border-b border-slate-200 bg-white flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="toggle-sidebar-button"
            className="p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
          >
            {sidebarOpen ? <PanelLeftClose className="w-5 h-5" /> : <PanelLeft className="w-5 h-5" />}
          </button>
        </div>
        <main className="flex-1 overflow-auto">{children}</main>
      </div>
      <Toaster position="top-right" />
    </div>
  );
};

function AppContent() {
  const [selectedAccount, setSelectedAccount] = useState("all");
  const [accounts, setAccounts] = useState([]);
  const [autoScanActive, setAutoScanActive] = useState(false);
  const { isAuthenticated } = useAuth();

  const fetchAccounts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/accounts`);
      setAccounts(response.data);
    } catch (err) { console.error("App error:", err);
      // silently fail
    }
  }, []);

  const fetchAutoScanStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auto-scan/status`);
      setAutoScanActive(response.data.active);
    } catch (err) { console.error("App error:", err);
      // silently fail
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAccounts();
      fetchAutoScanStatus();
    }
  }, [isAuthenticated, fetchAccounts, fetchAutoScanStatus]);

  const toggleAutoScan = useCallback(async () => {
    try {
      if (autoScanActive) {
        await axios.post(`${API}/auto-scan/stop`);
        toast.success("Auto-scan detenido");
        setAutoScanActive(false);
      } else {
        await axios.post(`${API}/auto-scan/start`);
        toast.success("Auto-scan iniciado");
        setAutoScanActive(true);
      }
    } catch (err) { console.error("App error:", err);
      toast.error("Error al cambiar auto-scan");
    }
  }, [autoScanActive]);

  const contextValue = useMemo(() => ({
    selectedAccount, setSelectedAccount, accounts, autoScanActive, toggleAutoScan, refreshAccounts: fetchAccounts
  }), [selectedAccount, accounts, autoScanActive, toggleAutoScan, fetchAccounts]);

  return (
    <AccountContext.Provider value={contextValue}>
      <Routes>
        <Route path="/login" element={isAuthenticated ? <Navigate to="/" /> : <LoginPage />} />
        <Route path="/" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><Dashboard /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/inbox" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><Inbox /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/accounts" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><AccountsPage /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/history" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><HistoryPage /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><ReportsPage /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/ai-training" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><AITrainingPage /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/intelligence" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><IntelligencePage /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/templates" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><TemplatesPage /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><UsersPage /></Suspense></Layout></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><SettingsPage /></Suspense></Layout></ProtectedRoute>} />
      </Routes>
    </AccountContext.Provider>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
